import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import {
  Close as CloseIcon,
  ChevronLeft as ChevronLeftIcon,
  Autorenew as AutorenewIcon,
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
} from "@mui/icons-material";
import {
  Task,
  Tag,
  User,
  RoomStatus,
  Reminder,
  RecurrenceRuleWithTodoList,
  CustomColumnValue,
} from "../types/types.ts";
import { TaskDetailPanel } from "./TaskDetailPanel.tsx";
import {
  PaletteColor,
  PaletteColorBg,
  PaletteColorDarkBg,
  PaletteColorHex,
} from "../types/PaletteColor.ts";
import { TaskUpdateDto } from "../hooks/useTodos.ts";
import { useDarkMode } from "../hooks/useDarkMode.ts";

interface RecurrenceManagementDialogProps {
  onClose: () => void;
  roomMembers: User[];
  availableTags: Tag[];
  color: PaletteColor;
  honbuId: number;
  roomId: number;
  currentListId: number;
  onUpdateTask: (taskId: string, updates: TaskUpdateDto) => Promise<void>;
  onDeleteTask: (taskId: string) => Promise<void>;
  addReminder: (
    taskId: string,
    reminderDateTime: Date
  ) => Promise<Reminder | null>;
  deleteReminder: (reminderId: string | number) => Promise<boolean>;
  onManageTags: () => void;
  roomStatuses: RoomStatus[];
  forceReload: () => void;
  isMobile?: boolean;
  initialTaskId?: string;
  initialRuleId?: number;
}

// ステータスに基づく色を取得
const getStatusColor = (
  status: string | undefined,
  roomStatuses: RoomStatus[]
): string => {
  const statusConfig = roomStatuses.find((s) => s.statusName === status);
  if (statusConfig) {
    const colorMap: Record<string, string> = {
      Gray: "#9CA3AF",
      Blue: "#3B82F6",
      Green: "#22C55E",
      Yellow: "#EAB308",
      Red: "#EF4444",
      Purple: "#A855F7",
      Pink: "#EC4899",
      Orange: "#F97316",
    };
    return colorMap[statusConfig.color] || "#9CA3AF";
  }
  // デフォルトの色
  switch (status) {
    case "未対応":
      return "#9CA3AF";
    case "対応中":
      return "#3B82F6";
    case "完了":
      return "#22C55E";
    case "非表示":
      return "#A855F7";
    default:
      return "#9CA3AF";
  }
};

interface RuleEntry {
  rule: RecurrenceRuleWithTodoList;
}

export function RecurrenceManagementDialog({
  onClose,
  roomMembers,
  availableTags,
  color,
  honbuId,
  roomId,
  currentListId,
  onUpdateTask,
  onDeleteTask,
  addReminder,
  deleteReminder,
  onManageTags,
  roomStatuses,
  forceReload,
  isMobile = false,
  initialTaskId,
  initialRuleId,
}: RecurrenceManagementDialogProps) {
  // モバイル表示用の状態
  const [mobileView, setMobileView] = useState<"list" | "detail">("list");
  // 検索フィルター
  const [searchTitle, setSearchTitle] = useState("");
  const [searchAssigneeId, setSearchAssigneeId] = useState<string>("");
  const [searchRequesterId, setSearchRequesterId] = useState<string>("");
  const [searchTagId, setSearchTagId] = useState<string>("");
  const [showFilters, setShowFilters] = useState(false);
  // 自動作成ルールのデータ
  const [recurrenceRules, setRecurrenceRules] = useState<
    RecurrenceRuleWithTodoList[]
  >([]);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedProjects, setExpandedProjects] = useState<Set<number>>(
    new Set()
  );
  // ダークモード状態を取得
  const isDarkMode = useDarkMode();

  // ダークモード対応の色を取得
  const selectedBgColor = isDarkMode
    ? PaletteColorDarkBg[color]
    : PaletteColorBg[color];

  // 自動作成ルールを取得
  useEffect(() => {
    const fetchRecurrenceRules = async () => {
      setIsLoading(true);
      try {
        const response = await fetch(
          `/api/recurrence-rules?roomId=${roomId}&honbuId=${honbuId}`
        );
        if (!response.ok) throw new Error("Failed to fetch recurrence rules");
        const data: RecurrenceRuleWithTodoList[] = await response.json();
        setRecurrenceRules(data);
      } catch (error) {
        console.error("Error fetching recurrence rules:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchRecurrenceRules();
  }, [roomId, honbuId]);

  // 現在のプロジェクトの一番上のルールを取得するヘルパー関数
  const getFirstRuleOfCurrentProject = useCallback(() => {
    // 現在のプロジェクトのルールを探す
    const currentProjectRules = recurrenceRules.filter(
      (rule) => rule.todoListId === currentListId
    );

    // 現在のプロジェクトにルールがあればその最初のルールを返す
    if (currentProjectRules.length > 0) {
      return currentProjectRules[0].recurringRuleId;
    }

    // 現在のプロジェクトにルールがなければ、全体の最初のルールを返す
    return recurrenceRules[0]?.recurringRuleId || null;
  }, [recurrenceRules, currentListId]);

  // contentsからテンプレートをパースするヘルパー
  const parseTemplate = useCallback((contents?: string) => {
    if (!contents) return { title: "繰り返し作成タスク", assignees: [], requesters: [], tags: [] };
    try {
      return JSON.parse(contents);
    } catch {
      return { title: "繰り返し作成タスク", assignees: [], requesters: [], tags: [] };
    }
  }, []);

  // フィルタリング済みのルール
  const filteredRecurrenceRules = useMemo(() => {
    return recurrenceRules.filter((rule) => {
      const template = parseTemplate(rule.contents);

      // タスク名（部分一致）
      if (searchTitle) {
        const title = (template.title || "").toLowerCase();
        if (!title.includes(searchTitle.toLowerCase())) return false;
      }

      // 担当者
      if (searchAssigneeId) {
        const assignees: any[] = template.assignees || [];
        if (!assignees.some((a: any) => a.id?.toString() === searchAssigneeId)) return false;
      }

      // 依頼者
      if (searchRequesterId) {
        const requesters: any[] = template.requesters || [];
        if (!requesters.some((r: any) => r.id?.toString() === searchRequesterId)) return false;
      }

      // タグ
      if (searchTagId) {
        const tags: any[] = template.tags || [];
        if (!tags.some((t: any) => t.id?.toString() === searchTagId)) return false;
      }

      return true;
    });
  }, [recurrenceRules, searchTitle, searchAssigneeId, searchRequesterId, searchTagId, parseTemplate]);

  // フィルターが有効かどうか
  const hasActiveFilters = searchTitle || searchAssigneeId || searchRequesterId || searchTagId;

  // フィルタークリア
  const clearFilters = useCallback(() => {
    setSearchTitle("");
    setSearchAssigneeId("");
    setSearchRequesterId("");
    setSearchTagId("");
  }, []);

  // プロジェクトごとにグループ化
  const rulesByProject = useMemo(() => {
    const grouped = new Map<string, RuleEntry[]>();

    filteredRecurrenceRules.forEach((rule) => {
      const projectKey = rule.todoListId
        ? `${rule.todoListId}-${rule.todoListName}`
        : "no-project";

      if (!grouped.has(projectKey)) {
        grouped.set(projectKey, []);
      }
      grouped.get(projectKey)!.push({ rule });
    });

    // 現在のプロジェクトを一番上に持ってくるようにソート
    const sortedEntries = Array.from(grouped.entries()).sort(
      ([keyA], [keyB]) => {
        const projectIdA = keyA.split("-")[0];
        const projectIdB = keyB.split("-")[0];

        // 現在のプロジェクトを一番上に
        if (projectIdA === currentListId.toString()) return -1;
        if (projectIdB === currentListId.toString()) return 1;

        // それ以外はプロジェクト名でソート
        return keyA.localeCompare(keyB);
      }
    );

    return new Map(sortedEntries);
  }, [filteredRecurrenceRules, currentListId]);

  // 選択状態を「ルールID」で管理
  const [selectedRuleId, setSelectedRuleId] = useState<number | null>(null);
  // ローカルのサブタスクリスト（インメモリ編集用）
  const [localChildTasks, setLocalChildTasks] = useState<any[]>([]);
  // カスタム項目の値（contentsから読み込み、編集時に更新）
  const [localCustomColumnValues, setLocalCustomColumnValues] = useState<CustomColumnValue[]>([]);
  // initialRuleIdが既に使用されたかを追跡
  const initialRuleIdUsedRef = useRef(false);

  // 初期選択ロジック: initialRuleIdがあればそれを使用、なければ現在のプロジェクトの一番上
  useEffect(() => {
    if (recurrenceRules.length === 0) {
      setSelectedRuleId(null);
      return;
    }

    // initialRuleIdが指定されていて、まだ使用されていない場合
    if (initialRuleId && !initialRuleIdUsedRef.current) {
      const exists = recurrenceRules.some(
        (r) => r.recurringRuleId === initialRuleId
      );
      if (exists) {
        setSelectedRuleId(initialRuleId);
        initialRuleIdUsedRef.current = true; // 使用済みにマーク
        return;
      }
    }

    // 現在選択されているルールが存在するかチェック
    if (selectedRuleId) {
      const exists = recurrenceRules.some(
        (r) => r.recurringRuleId === selectedRuleId
      );
      if (exists) {
        return; // 現在の選択を維持
      }
    }

    // どのルールも選択されていない、または選択中のルールが削除された場合
    // 現在のプロジェクトの一番上のルールを選択
    const firstRuleId = getFirstRuleOfCurrentProject();
    setSelectedRuleId(firstRuleId);
  }, [recurrenceRules, selectedRuleId, getFirstRuleOfCurrentProject]);

  // 現在選択されているルール
  const activeRule = useMemo(
    () => recurrenceRules.find((r) => r.recurringRuleId === selectedRuleId),
    [recurrenceRules, selectedRuleId]
  );

  // Contentsから仮想タスクを作成
  const virtualTask = useMemo((): Task | null => {
    console.log(
      "[RecurrenceManagement] Creating virtualTask, activeRule:",
      activeRule
    );
    if (!activeRule || !activeRule.recurringRuleId) {
      console.log("[RecurrenceManagement] No active rule, returning null");
      return null;
    }

    const baseTask: Task = {
      id:
        activeRule.originalTaskId?.toString() ||
        `virtual-${activeRule.recurringRuleId}`,
      type: "single",
      title: "繰り返し作成タスク",
      completed: false,
      status: "未対応",
      priority: "なし",
      startDate: "",
      endDate: "",
      createdAt: new Date().toISOString(),
      lastUpdateAt: new Date().toISOString(),
      assignees: [],
      requesters: [],
      tags: [],
      memo: "",
      notification: false,
      starred: false,
      reminders: [],
      recurrenceRules: [activeRule],
      childTasks: [],
    };

    if (!activeRule.contents) return baseTask;

    try {
      const template = JSON.parse(activeRule.contents);
      return {
        ...baseTask,
        title: template.title || baseTask.title,
        memo: template.memo || "",
        priority: template.priority || "なし",
        notification: template.notification ?? false,
        assignees: (template.assignees || []).map((a: any) => ({
          id: a.id.toString(),
          name: a.name,
        })),
        requesters: (template.requesters || []).map((r: any) => ({
          id: r.id.toString(),
          name: r.name,
        })),
        tags: (template.tags || []).map((t: any) => ({
          id: t.id,
          name: t.name,
          color: t.color,
        })),
        childTasks: template.childTasks || [],
      } as Task;
    } catch (e) {
      console.error("Failed to parse contents:", e);
      return baseTask;
    }
  }, [activeRule]);

  // localChildTasksを反映した仮想タスク
  const virtualTaskWithLocalChildren = useMemo((): Task | null => {
    if (!virtualTask) return null;
    return { ...virtualTask, childTasks: localChildTasks };
  }, [virtualTask, localChildTasks]);

  // ダイアログを閉じる
  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  // データを再読み込み
  const handleForceReload = useCallback(async () => {
    const response = await fetch(
      `/api/recurrence-rules?roomId=${roomId}&honbuId=${honbuId}`
    );
    if (response.ok) {
      const data: RecurrenceRuleWithTodoList[] = await response.json();
      setRecurrenceRules(data);
    }
  }, [roomId, honbuId]);

  // サブタスクを再帰的に抽出する関数（Contentsから）
  const extractChildTasksFromContents = useCallback(
    (contents?: string): any[] => {
      if (!contents) return [];

      try {
        const template = JSON.parse(contents);
        const ts = Date.now();
        return (template.childTasks || []).map((ct: any, i: number) => ({
          ...ct,
          id: ct.id ?? `temp-${ts}-${i}`,
        }));
      } catch (e) {
        return [];
      }
    },
    []
  );

  // ルール切り替え時にlocalChildTasksとlocalCustomColumnValuesを初期化
  useEffect(() => {
    setLocalChildTasks(extractChildTasksFromContents(activeRule?.contents));
    // contentsからカスタム項目の値を復元
    if (activeRule?.contents) {
      try {
        const template = JSON.parse(activeRule.contents);
        const cvs: CustomColumnValue[] = (template.customColumnValues || []).map(
          (cv: { customColumnId: number; value: string }) => ({
            taskId: 0,
            customColumnId: cv.customColumnId,
            value: cv.value ?? null,
          })
        );
        setLocalCustomColumnValues(cvs);
      } catch {
        setLocalCustomColumnValues([]);
      }
    } else {
      setLocalCustomColumnValues([]);
    }
  }, [selectedRuleId, activeRule?.contents, extractChildTasksFromContents]);

  // ルールのcontentsをAPIに保存するヘルパー
  const saveRuleContents = useCallback(
    async (contents: string) => {
      if (!activeRule) return;
      const response = await fetch(
        `/api/recurrence/${activeRule.recurringRuleId}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            recurringRuleId: activeRule.recurringRuleId,
            recurrenceType: activeRule.recurrenceType,
            recurrenceValue: activeRule.recurrenceValue,
            intervalMonths: activeRule.intervalMonths ?? 1,
            recurrenceMonth: activeRule.recurrenceMonth,
            periodStartDate: activeRule.periodStartDate,
            periodEndDate: activeRule.periodEndDate,
            dueDateType: activeRule.dueDateType,
            dueDateRelativeDays: activeRule.dueDateRelativeDays,
            dueDateNextDayOfMonth: activeRule.dueDateNextDayOfMonth,
            isDueDateDuration: activeRule.isDueDateDuration,
            dueDateRelativeDaysEnd: activeRule.dueDateRelativeDaysEnd,
            dueDateNextDayOfMonthEnd: activeRule.dueDateNextDayOfMonthEnd,
            notificationEnabled: activeRule.notificationEnabled,
            reminderType: activeRule.reminderType,
            reminderRelativeDays: activeRule.reminderRelativeDays,
            reminderNextDayOfMonth: activeRule.reminderNextDayOfMonth,
            contents,
          }),
        }
      );
      if (!response.ok) throw new Error("Failed to save recurrence contents");
    },
    [activeRule]
  );

  // サブタスクを追加するハンドラ（即時API保存）
  const handleAddSubtaskToRecurrence = useCallback(
    async (_parentTaskId: string, title: string): Promise<string | null> => {
      if (!activeRule) return null;
      const newSubtask = {
        id: `temp-${Date.now()}`,
        title,
        childTasks: [],
      };
      const newChildTasks = [...localChildTasks, newSubtask];
      setLocalChildTasks(newChildTasks);
      const currentTemplate = activeRule.contents
        ? JSON.parse(activeRule.contents)
        : {};
      await saveRuleContents(
        JSON.stringify({ ...currentTemplate, childTasks: newChildTasks })
      );
      await handleForceReload();
      return newSubtask.id;
    },
    [activeRule, localChildTasks, saveRuleContents, handleForceReload]
  );

  // カスタム項目値変更ハンドラー（contentsに即時保存）
  const handleCustomColumnValueChange = useCallback(
    async (columnId: number, value: string) => {
      if (!activeRule) return;
      const updated = localCustomColumnValues.some(v => v.customColumnId === columnId)
        ? localCustomColumnValues.map(v => v.customColumnId === columnId ? { ...v, value } : v)
        : [...localCustomColumnValues, { taskId: 0, customColumnId: columnId, value: value ?? null }];
      setLocalCustomColumnValues(updated);
      const currentTemplate = activeRule.contents ? JSON.parse(activeRule.contents) : {};
      await saveRuleContents(JSON.stringify({
        ...currentTemplate,
        customColumnValues: updated
          .filter(v => v.value != null && v.value !== "")
          .map(v => ({ customColumnId: v.customColumnId, value: v.value })),
      }));
    },
    [activeRule, localCustomColumnValues, saveRuleContents]
  );

  // 自動作成ルールの更新（親タスク・サブタスクを区別）
  const handleUpdateRecurrence = useCallback(
    async (taskId: string, updates: TaskUpdateDto) => {
      if (!activeRule) return;

      const isParentTask =
        !virtualTaskWithLocalChildren ||
        taskId === virtualTaskWithLocalChildren.id;

      const mapAssignees = (ids: number[]) =>
        ids.map((id) => {
          const member = roomMembers.find((m) => m.id === id.toString());
          return { id, name: member?.name || "" };
        });
      const mapTags = (ids: number[]) =>
        ids
          .map((tagId) => {
            const tag = availableTags.find((t) => t.id === tagId);
            return tag ? { id: tagId, name: tag.name, color: tag.color } : null;
          })
          .filter((t): t is any => t !== null);

      const currentCustomColumnValues = localCustomColumnValues
        .filter(v => v.value != null && v.value !== "")
        .map(v => ({ customColumnId: v.customColumnId, value: v.value }));

      let taskTemplate: any;

      if (isParentTask) {
        // 親タスクの更新
        taskTemplate = {
          title: updates.title,
          memo: updates.memo || "",
          priority: updates.priority,
          notification: updates.notification,
          assignees: mapAssignees(updates.assigneeHonbuIds || []),
          requesters: mapAssignees(updates.requesterHonbuIds || []),
          tags: mapTags(updates.tagIds || []),
          childTasks: localChildTasks,
          customColumnValues: currentCustomColumnValues,
        };
      } else {
        // サブタスクの更新
        const currentTemplate = activeRule.contents
          ? JSON.parse(activeRule.contents)
          : {};
        const updatedSubtask = {
          id: taskId,
          title: updates.title,
          memo: updates.memo || "",
          priority: updates.priority,
          assignees: mapAssignees(updates.assigneeHonbuIds || []),
          requesters: mapAssignees(updates.requesterHonbuIds || []),
          tags: mapTags(updates.tagIds || []),
          childTasks:
            localChildTasks.find((ct) => ct.id === taskId)?.childTasks || [],
        };
        const updatedChildTasks = localChildTasks.map((ct) =>
          ct.id === taskId ? updatedSubtask : ct
        );
        taskTemplate = {
          ...currentTemplate,
          childTasks: updatedChildTasks,
          customColumnValues: currentCustomColumnValues,
        };
      }

      await saveRuleContents(JSON.stringify(taskTemplate));

      // データを再取得
      const refetchResponse = await fetch(
        `/api/recurrence-rules?roomId=${roomId}&honbuId=${honbuId}`
      );
      if (refetchResponse.ok) {
        const data: RecurrenceRuleWithTodoList[] = await refetchResponse.json();
        setRecurrenceRules(data);
      }
    },
    [
      activeRule,
      roomMembers,
      availableTags,
      roomId,
      honbuId,
      localChildTasks,
      localCustomColumnValues,
      virtualTaskWithLocalChildren,
      saveRuleContents,
    ]
  );

  // 繰り返しルールの削除
  const handleDeleteRecurrence = useCallback(
    async (ruleId: number): Promise<boolean> => {
      const response = await fetch(`/api/recurrence/${ruleId}`, {
        method: "DELETE",
      });
      if (!response.ok) return false;

      // データを再取得
      const refetchResponse = await fetch(
        `/api/recurrence-rules?roomId=${roomId}&honbuId=${honbuId}`
      );
      if (refetchResponse.ok) {
        const data: RecurrenceRuleWithTodoList[] = await refetchResponse.json();
        setRecurrenceRules(data);

        // 削除したルールが現在選択中だった場合、次のルールを選択
        if (selectedRuleId === ruleId && data.length > 0) {
          // 現在のプロジェクトの一番上のルールを選択
          const currentProjectRules = data.filter(
            (rule) => rule.todoListId === currentListId
          );
          const nextRuleId =
            currentProjectRules.length > 0
              ? currentProjectRules[0].recurringRuleId
              : data[0].recurringRuleId;
          setSelectedRuleId(nextRuleId);
        } else if (data.length === 0) {
          setSelectedRuleId(null);
        }
      }

      return true;
    },
    [selectedRuleId, roomId, honbuId, currentListId]
  );

  // ルール削除（削除ボタンを押した時）
  const handleDeleteRule = useCallback(
    async (taskId: string) => {
      if (!activeRule) return;

      const isParentTask =
        !virtualTaskWithLocalChildren ||
        taskId === virtualTaskWithLocalChildren.id;

      if (isParentTask) {
        // 親タスク（ルール全体）を削除
        await handleDeleteRecurrence(activeRule.recurringRuleId);
      } else {
        // サブタスクのみ削除：ルール contents から除去して保存
        const updatedChildTasks = localChildTasks.filter(
          (ct) => ct.id !== taskId
        );
        setLocalChildTasks(updatedChildTasks);
        const currentTemplate = activeRule.contents
          ? JSON.parse(activeRule.contents)
          : {};
        await saveRuleContents(
          JSON.stringify({ ...currentTemplate, childTasks: updatedChildTasks })
        );
        await handleForceReload();
      }
    },
    [
      activeRule,
      virtualTaskWithLocalChildren,
      localChildTasks,
      handleDeleteRecurrence,
      saveRuleContents,
      handleForceReload,
    ]
  );

  // プロジェクト展開のトグル
  const toggleProjectExpand = useCallback((projectId: number) => {
    setExpandedProjects((prev) => {
      const next = new Set(prev);
      if (next.has(projectId)) {
        next.delete(projectId);
      } else {
        next.add(projectId);
      }
      return next;
    });
  }, []);

  // 初期展開: すべてのプロジェクトを展開
  useEffect(() => {
    const allProjectIds = Array.from(rulesByProject.entries()).map(
      ([key, rules]) => {
        return rules[0].rule.todoListId || 0;
      }
    );
    setExpandedProjects(new Set(allProjectIds));
  }, [rulesByProject]);

  const getRuleDisplayText = (rule: any): string => {
    let text = "";
    if (rule.recurrenceType === "Daily") text = "毎日";
    else if (rule.recurrenceType === "Monthly") {
      const day =
        rule.recurrenceValue === 32 ? "最終" : rule.recurrenceValue ?? 1;
      const interval = rule.intervalMonths ?? 1;
      if (interval <= 1) {
        text = `毎月${day}日`;
      } else {
        text = `${interval}ヶ月ごとの${day}日`;
      }
    } else if (rule.recurrenceType === "Weekly") {
      const days = ["日", "月", "火", "水", "木", "金", "土"];
      text = `毎週${days[rule.recurrenceValue ?? 0]}曜日`;
    } else if (rule.recurrenceType === "Yearly") {
      const month = rule.recurrenceMonth ?? 1;
      const day =
        rule.recurrenceValue === 32 ? "最終" : rule.recurrenceValue ?? 1;
      text = `毎年${month}月${day}日`;
    } else return "無効なルール";
    return text;
  };

  const handleSelectRule = (ruleId: number) => {
    setSelectedRuleId(ruleId);
    if (isMobile) {
      setMobileView("detail");
    }
  };

  const handleBack = () => {
    setMobileView("list");
  };

  // モバイル版
  if (isMobile) {
    return (
      <div className="fixed inset-0 bg-white z-50 flex flex-col">
        {mobileView === "list" ? (
          <>
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="font-bold text-lg flex items-center gap-2">
                <AutorenewIcon />
                繰り返し作成一覧
              </h3>
              <button
                onClick={handleClose}
                className="p-2 rounded-full hover:bg-gray-100"
              >
                <CloseIcon />
              </button>
            </div>
            {/* モバイル版 検索フィルター */}
            <div className="border-b">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`w-full flex items-center gap-1 px-4 py-2 text-sm ${
                  hasActiveFilters ? "text-blue-600 font-bold" : "text-gray-500"
                }`}
              >
                <FilterListIcon sx={{ fontSize: 18 }} />
                検索フィルター
                {hasActiveFilters && (
                  <span
                    className="ml-auto text-blue-500"
                    onClick={(e) => { e.stopPropagation(); clearFilters(); }}
                  >
                    クリア
                  </span>
                )}
              </button>
              {showFilters && (
                <div className="px-4 pb-3 space-y-2">
                  <div className="relative">
                    <SearchIcon sx={{ fontSize: 16 }} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      placeholder="タスク名で検索..."
                      value={searchTitle}
                      onChange={(e) => setSearchTitle(e.target.value)}
                      className="w-full pl-8 pr-2 py-1.5 text-sm border rounded bg-white focus:outline-none focus:ring-1 focus:ring-blue-400"
                    />
                  </div>
                  <select
                    value={searchAssigneeId}
                    onChange={(e) => setSearchAssigneeId(e.target.value)}
                    className="w-full px-2 py-1.5 text-sm border rounded bg-white focus:outline-none focus:ring-1 focus:ring-blue-400"
                  >
                    <option value="">担当者（すべて）</option>
                    {roomMembers.map((m) => (
                      <option key={m.id} value={m.id}>{m.name}</option>
                    ))}
                  </select>
                  <select
                    value={searchRequesterId}
                    onChange={(e) => setSearchRequesterId(e.target.value)}
                    className="w-full px-2 py-1.5 text-sm border rounded bg-white focus:outline-none focus:ring-1 focus:ring-blue-400"
                  >
                    <option value="">依頼者（すべて）</option>
                    {roomMembers.map((m) => (
                      <option key={m.id} value={m.id}>{m.name}</option>
                    ))}
                  </select>
                  <select
                    value={searchTagId}
                    onChange={(e) => setSearchTagId(e.target.value)}
                    className="w-full px-2 py-1.5 text-sm border rounded bg-white focus:outline-none focus:ring-1 focus:ring-blue-400"
                  >
                    <option value="">タグ（すべて）</option>
                    {availableTags.map((t) => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
            <div className="flex-1 overflow-y-auto">
              {isLoading ? (
                <div className="p-4 text-center text-gray-500">
                  読み込み中...
                </div>
              ) : recurrenceRules.length === 0 ? (
                <div className="p-4 text-center text-gray-500">
                  繰り返し作成ルールが設定されていません
                </div>
              ) : (
                Array.from(rulesByProject.entries()).map(
                  ([projectKey, rules]) => {
                    const firstRule = rules[0].rule;
                    const projectName =
                      firstRule.todoListName || "プロジェクトなし";
                    const projectId = firstRule.todoListId || 0;

                    return (
                      <div key={projectKey}>
                        <button
                          onClick={() => toggleProjectExpand(projectId)}
                          className="w-full flex items-center gap-2 p-3 hover:bg-gray-100 text-left border-b bg-gray-50"
                        >
                          {expandedProjects.has(projectId) ? (
                            <ExpandMoreIcon fontSize="small" />
                          ) : (
                            <ChevronRightIcon fontSize="small" />
                          )}
                          <span className="text-sm font-medium">
                            {projectName}
                          </span>
                          <span className="text-xs text-gray-500">
                            ({rules.length})
                          </span>
                        </button>

                        {expandedProjects.has(projectId) &&
                          rules.map(({ rule }) => {
                            const template = rule.contents
                              ? (() => {
                                  try {
                                    return JSON.parse(rule.contents);
                                  } catch {
                                    return { title: "繰り返し作成タスク" };
                                  }
                                })()
                              : { title: "繰り返し作成タスク" };

                            return (
                              <div
                                key={rule.recurringRuleId}
                                onClick={() =>
                                  handleSelectRule(rule.recurringRuleId)
                                }
                                className={`flex border-b cursor-pointer ${
                                  selectedRuleId === rule.recurringRuleId
                                    ? ""
                                    : isDarkMode
                                    ? "hover:bg-gray-700"
                                    : "hover:bg-gray-100"
                                }`}
                                style={
                                  selectedRuleId === rule.recurringRuleId
                                    ? { backgroundColor: selectedBgColor }
                                    : undefined
                                }
                              >
                                <div
                                  className="w-1 flex-shrink-0"
                                  style={{
                                    backgroundColor:
                                      rule.todoListId === currentListId
                                        ? PaletteColorHex[color]
                                        : "#9CA3AF",
                                  }}
                                />
                                <div className="flex-1 p-3">
                                  <div className="text-[10px] leading-none text-gray-400">
                                    ID: {rule.recurringRuleId}
                                  </div>
                                  <div
                                    className={`font-medium text-sm truncate mt-0.5 ${
                                      selectedRuleId === rule.recurringRuleId
                                        ? isDarkMode
                                          ? "text-white"
                                          : "text-gray-900"
                                        : ""
                                    }`}
                                  >
                                    {template.title}
                                  </div>
                                  <div className="text-xs mt-1 flex items-center gap-1 text-blue-500 font-bold">
                                    <AutorenewIcon sx={{ fontSize: 14 }} />
                                    {getRuleDisplayText(rule)}
                                  </div>
                                  {template.childTasks &&
                                    template.childTasks.length > 0 && (
                                      <div className="text-xs mt-1 text-gray-500">
                                        {template.childTasks.length}
                                        件のサブタスクがあります
                                      </div>
                                    )}
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    );
                  }
                )
              )}
            </div>
          </>
        ) : (
          <>
            <div className="flex items-center p-4 border-b">
              <button
                onClick={handleBack}
                className="p-2 rounded-full hover:bg-gray-100 mr-2"
              >
                <ChevronLeftIcon />
              </button>
              <h3 className="font-bold">繰り返し作成ルール編集</h3>
            </div>
            <div className="flex-1 overflow-y-auto">
              {isLoading ? (
                <div className="flex items-center justify-center h-full">
                  <p>読み込み中...</p>
                </div>
              ) : virtualTaskWithLocalChildren && virtualTaskWithLocalChildren.id ? (
                <TaskDetailPanel
                  task={virtualTaskWithLocalChildren}
                  isSubtask={false}
                  parentForContext={virtualTaskWithLocalChildren}
                  onUpdateTask={handleUpdateRecurrence}
                  onUpdateStatus={() => {}} // ルール編集中はステータス変更不可
                  onDeleteTask={handleDeleteRule} // ルール削除
                  roomMembers={roomMembers}
                  availableTags={availableTags}
                  onManageTags={onManageTags}
                  color={color}
                  addReminder={async () => null} // ルールにはリマインダーなし
                  deleteReminder={async () => false} // ルールにはリマインダーなし
                  addRecurrence={async () => null}
                  deleteRecurrence={handleDeleteRecurrence}
                  hideDatesAndReminders={true}
                  hideStatusAndTemplate={true}
                  displayMode="fullscreen"
                  roomStatuses={roomStatuses}
                  forceReload={handleForceReload}
                  honbuId={honbuId}
                  roomId={roomId}
                  showSubtaskSelector={true}
                  fromRecurrenceManagement={true}
                  onAddSubtask={handleAddSubtaskToRecurrence}
                  listId={currentListId}
                  initialCustomColumnValues={localCustomColumnValues}
                  onCustomColumnValueChange={handleCustomColumnValueChange}
                />
              ) : (
                <div className="flex items-center justify-center h-full">
                  <p>ルールを選択してください</p>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    );
  }

  // PC版
  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex justify-end"
      onClick={(e) => {
        e.stopPropagation();
        handleClose();
      }}
    >
      <div
        className="w-full md:w-[768px] h-full bg-white shadow-lg animate-slide-in-right flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* ヘッダー */}
        <div className="flex-shrink-0 p-4 border-b flex justify-between items-center">
          <h3 className="font-bold flex items-center gap-2">
            <AutorenewIcon />
            繰り返し作成一覧
          </h3>
          <button
            onClick={handleClose}
            className="p-2 rounded-full hover:bg-gray-100"
          >
            <CloseIcon />
          </button>
        </div>

        {/* メインエリア */}
        <div className="flex flex-1 min-h-0">
          {/* 左側：タスク一覧 */}
          <div className="w-1/3 border-r bg-gray-50 h-full flex flex-col overflow-x-hidden">
            {/* 検索フィルター */}
            <div className="flex-shrink-0 border-b">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`w-full flex items-center gap-1 px-3 py-2 text-xs hover:bg-gray-100 ${
                  hasActiveFilters ? "text-blue-600 font-bold" : "text-gray-500"
                }`}
              >
                <FilterListIcon sx={{ fontSize: 16 }} />
                検索フィルター
                {hasActiveFilters && (
                  <span
                    className="ml-auto text-blue-500 hover:text-red-500"
                    onClick={(e) => { e.stopPropagation(); clearFilters(); }}
                  >
                    クリア
                  </span>
                )}
              </button>
              {showFilters && (
                <div className="px-3 pb-2 space-y-1.5">
                  <div className="relative">
                    <SearchIcon sx={{ fontSize: 14 }} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      placeholder="タスク名で検索..."
                      value={searchTitle}
                      onChange={(e) => setSearchTitle(e.target.value)}
                      className="w-full pl-7 pr-2 py-1 text-xs border rounded bg-white focus:outline-none focus:ring-1 focus:ring-blue-400"
                    />
                  </div>
                  <select
                    value={searchAssigneeId}
                    onChange={(e) => setSearchAssigneeId(e.target.value)}
                    className="w-full px-2 py-1 text-xs border rounded bg-white focus:outline-none focus:ring-1 focus:ring-blue-400"
                  >
                    <option value="">担当者（すべて）</option>
                    {roomMembers.map((m) => (
                      <option key={m.id} value={m.id}>{m.name}</option>
                    ))}
                  </select>
                  <select
                    value={searchRequesterId}
                    onChange={(e) => setSearchRequesterId(e.target.value)}
                    className="w-full px-2 py-1 text-xs border rounded bg-white focus:outline-none focus:ring-1 focus:ring-blue-400"
                  >
                    <option value="">依頼者（すべて）</option>
                    {roomMembers.map((m) => (
                      <option key={m.id} value={m.id}>{m.name}</option>
                    ))}
                  </select>
                  <select
                    value={searchTagId}
                    onChange={(e) => setSearchTagId(e.target.value)}
                    className="w-full px-2 py-1 text-xs border rounded bg-white focus:outline-none focus:ring-1 focus:ring-blue-400"
                  >
                    <option value="">タグ（すべて）</option>
                    {availableTags.map((t) => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
            <div className="flex-1 overflow-y-auto">
            {isLoading ? (
              <div className="p-4 text-center text-gray-500">読み込み中...</div>
            ) : recurrenceRules.length === 0 ? (
              <div className="p-4 text-center text-gray-500">
                繰り返し作成ルールが設定されていません
              </div>
            ) : (
              Array.from(rulesByProject.entries()).map(
                ([projectKey, rules]) => {
                  const firstRule = rules[0].rule;
                  const projectName =
                    firstRule.todoListName || "プロジェクトなし";
                  const projectId = firstRule.todoListId || 0;

                  return (
                    <div key={projectKey}>
                      <button
                        onClick={() => toggleProjectExpand(projectId)}
                        className="w-full flex items-center gap-2 p-3 hover:bg-gray-100 text-left border-b bg-gray-100"
                      >
                        {expandedProjects.has(projectId) ? (
                          <ExpandMoreIcon fontSize="small" />
                        ) : (
                          <ChevronRightIcon fontSize="small" />
                        )}
                        <span className="text-sm font-medium truncate">
                          {projectName}
                        </span>
                        <span className="text-xs text-gray-500 flex-shrink-0">
                          ({rules.length})
                        </span>
                      </button>

                      {expandedProjects.has(projectId) &&
                        rules.map(({ rule }) => {
                          const template = rule.contents
                            ? (() => {
                                try {
                                  return JSON.parse(rule.contents);
                                } catch {
                                  return { title: "繰り返し作成タスク" };
                                }
                              })()
                            : { title: "繰り返し作成タスク" };

                          return (
                            <div
                              key={rule.recurringRuleId}
                              onClick={() =>
                                setSelectedRuleId(rule.recurringRuleId)
                              }
                              className={`flex border-b cursor-pointer ${
                                selectedRuleId === rule.recurringRuleId
                                  ? ""
                                  : isDarkMode
                                  ? "hover:bg-gray-700"
                                  : "hover:bg-gray-100"
                              }`}
                              style={
                                selectedRuleId === rule.recurringRuleId
                                  ? { backgroundColor: selectedBgColor }
                                  : undefined
                              }
                            >
                              <div
                                className="w-1 flex-shrink-0"
                                style={{
                                  backgroundColor:
                                    rule.todoListId === currentListId
                                      ? PaletteColorHex[color]
                                      : "#9CA3AF",
                                }}
                              />
                              <div className="flex-1 p-3 min-w-0">
                                <div className="text-[10px] leading-none text-gray-400">
                                  ID: {rule.recurringRuleId}
                                </div>
                                <div
                                  className={`font-medium text-sm truncate mt-0.5 ${
                                    selectedRuleId === rule.recurringRuleId
                                      ? isDarkMode
                                        ? "text-white"
                                        : "text-gray-900"
                                      : ""
                                  }`}
                                >
                                  {template.title}
                                </div>
                                <div className="text-xs mt-1 flex items-center gap-1 text-blue-500 font-bold">
                                  <AutorenewIcon sx={{ fontSize: 14 }} />
                                  {getRuleDisplayText(rule)}
                                </div>
                                {template.childTasks &&
                                  template.childTasks.length > 0 && (
                                    <div className="text-xs mt-1 text-gray-500">
                                      {template.childTasks.length}
                                      件のサブタスクがあります
                                    </div>
                                  )}
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  );
                }
              )
            )}
            </div>
          </div>

          {/* 右側：詳細パネル */}
          <div className="w-2/3 h-full overflow-y-auto">
            {isLoading ? (
              <div className="flex items-center justify-center h-full">
                <p>読み込み中...</p>
              </div>
            ) : virtualTaskWithLocalChildren && virtualTaskWithLocalChildren.id ? (
              <TaskDetailPanel
                task={virtualTaskWithLocalChildren}
                isSubtask={false}
                parentForContext={virtualTaskWithLocalChildren}
                onUpdateTask={handleUpdateRecurrence}
                onUpdateStatus={() => {}} // ルール編集中はステータス変更不可
                onDeleteTask={handleDeleteRule} // ルール削除
                roomMembers={roomMembers}
                availableTags={availableTags}
                onManageTags={onManageTags}
                color={color}
                addReminder={async () => null} // ルールにはリマインダーなし
                deleteReminder={async () => false} // ルールにはリマインダーなし
                addRecurrence={async () => null}
                deleteRecurrence={handleDeleteRecurrence}
                hideDatesAndReminders={true}
                hideStatusAndTemplate={true}
                displayMode="panel"
                roomStatuses={roomStatuses}
                forceReload={handleForceReload}
                honbuId={honbuId}
                roomId={roomId}
                showSubtaskSelector={true}
                fromRecurrenceManagement={true}
                onAddSubtask={handleAddSubtaskToRecurrence}
                listId={currentListId}
                initialCustomColumnValues={localCustomColumnValues}
                onCustomColumnValueChange={handleCustomColumnValueChange}
              />
            ) : (
              <div className="p-4 flex items-center justify-center h-full text-gray-500">
                繰り返し作成ルールを選択してください
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
